import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportTableQuery } from '../../queries';
import { ClaimsActivityReportTableParams } from '../ClaimsActivityReport.types';

export const QUERY_KEY = 'CLAIMS_ACTIVITY_REPORT_QUERY';

const MIN_SEARCH_LEN = 3;
const useClaimsActivityReportQuery = (params: Partial<ClaimsActivityReportTableParams>) => {
  const formattedStartDate = useMemo(() => {
    if (!params.startDate) {
      return null;
    }
    const parsedDate = dayjs(params.startDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.startDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.endDate) {
      return null;
    }
    const parsedDate = dayjs(params.endDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.endDate]);
  const submitStartDate = useMemo(() => {
    return params.isClaimSubmitMode ? formattedStartDate : null;
  }, [formattedStartDate, params.isClaimSubmitMode]);

  const submitEndDate = useMemo(() => {
    return params.isClaimSubmitMode ? formattedEndDate : null;
  }, [formattedEndDate, params.isClaimSubmitMode]);

  const paidStartDate = useMemo(() => {
    return params.isClaimSubmitMode ? null : formattedStartDate;
  }, [formattedStartDate, params.isClaimSubmitMode]);
  const padEndDate = useMemo(() => {
    return params.isClaimSubmitMode ? null : formattedEndDate;
  }, [formattedEndDate, params.isClaimSubmitMode]);

  const currentSearch = useMemo(() => {
    if (!!params?.searchString && params?.searchString?.length >= MIN_SEARCH_LEN) {
      return params.searchString;
    }
    return null;
  }, [params.searchString]);
  const getEmployerIdParam = useGetEmployerIdParam();
  const employerIdsString = useMemo(() => params?.employerIds?.join(','), [params?.employerIds]);

  const base = useBaseReportTableQuery(
    API_URL.REPORTS_CLAIM_ACTIVITY_REPORT_BREAKDOWN,
    [
      QUERY_KEY,
      formattedStartDate,
      formattedEndDate,
      params?.claimStatuses?.join(','),
      params?.claimTypes?.join(','),
      currentSearch,
      params.employerId,
      params.isClaimSubmitMode,
      employerIdsString,
    ],
    {
      ...getEmployerIdParam(params.employerId, params.employerIds),

      show_ssn: true,
      isSSNShowed: params.isSSNShowed,

      claim_submit_start_date: submitStartDate,
      claim_submit_end_date: submitEndDate,

      claim_paid_start_date: paidStartDate,
      claim_paid_end_date: padEndDate,

      search_value: currentSearch,
      claim_statuses:
        params?.claimStatuses && !params?.claimStatuses.includes(ALL_OPTION_KEY)
          ? params?.claimStatuses.join(',')
          : undefined,
      claim_types:
        params?.claimTypes && !params?.claimTypes.includes(ALL_OPTION_KEY)
          ? params?.claimTypes.join(',')
          : undefined,
    },
    {
      isMultipleEmployers: params.isMultipleEmployer,
      isPreviewMode: params.isPreviewMode,
    }
  );

  useEffect(() => {
    base.onChangePlanOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.employerId, employerIdsString]);

  useEffect(() => {
    base.setPage(0);
    base.setRawContentList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.employerId,
    employerIdsString,
    params?.startDate,
    params?.endDate,
    params?.claimStatuses,
    params?.searchString,
    params.employerId,
    params?.claimTypes,
  ]);
  return base;
};

export default useClaimsActivityReportQuery;
